import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { clearCards, computeCardStatus, fetchActiveCards, getData, giveCard, isCardsEnabled, resolveMediaUrl } from "../api";
import type { PlayerCardRow } from "../api";
import theme from "../styles/theme";

type PlayerRow = {
  id: string | number;
  username: string;
  nom_complet: string;
  photo: string;
};

type CardAction = "yellow" | "red" | "clear";

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

export default function CoachCards() {
  const coach = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const [players, setPlayers] = useState<PlayerRow[]>([]);
  const [cards, setCards] = useState<PlayerCardRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<null | { player: PlayerRow; action: CardAction }>(null);
  const [reason, setReason] = useState("");

  const cardsByUser = useMemo(() => {
    const map = new Map<string, PlayerCardRow[]>();
    cards.forEach((c) => {
      const k = String(c.user_id);
      map.set(k, [...(map.get(k) || []), c]);
    });
    return map;
  }, [cards]);

  useEffect(() => {
    (async () => {
      try {
        if (!(await isCardsEnabled())) {
          setFeatureEnabled(false);
          return;
        }
        const [usersRes, activeCards] = await Promise.all([
          getData("users", { where: "role = 'player'" }),
          fetchActiveCards(),
        ]);
        const users = usersRes?.data || [];
        const withProfiles: PlayerRow[] = await Promise.all(
          users.map(async (u: any) => {
            const profileRes = await getData("profiles", { where: `user_id = '${u.id}'` });
            const profile = profileRes?.data?.[0] || {};
            return {
              id: u.id,
              username: u.username,
              nom_complet: profile.nom_complet || u.username,
              photo: profile.photo || "",
            };
          })
        );
        withProfiles.sort((a, b) => a.nom_complet.localeCompare(b.nom_complet, "fr"));
        setPlayers(withProfiles);
        setCards(activeCards);
      } catch (e: any) {
        setError(e?.message || "Erreur de chargement.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openAction = (player: PlayerRow, action: CardAction) => {
    setReason("");
    setPending({ player, action });
  };

  const confirmAction = async () => {
    if (!pending) return;
    setBusy(true);
    const { player, action } = pending;
    const ok =
      action === "clear"
        ? await clearCards(player.id)
        : await giveCard(player.id, action, coach?.username || "coach", reason.trim());
    if (ok) {
      setCards(await fetchActiveCards());
      setPending(null);
    } else {
      setError("L'action n'a pas pu être enregistrée. Réessaie.");
      setPending(null);
    }
    setBusy(false);
  };

  const pendingStatus = pending ? computeCardStatus(cardsByUser.get(String(pending.player.id)) || []) : null;

  if (!featureEnabled) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, pb: 10 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 0.5 }}>
          Cartons
        </Typography>
        <Alert severity="info" sx={{ mt: 2, maxWidth: 500 }}>
          La fonctionnalité Cartons est désactivée par un administrateur (Réglages — Fonctionnalités).
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: 10, maxWidth: 780 }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.primary.main, mb: 0.5 }}>
        Cartons
      </Typography>
      <Typography sx={{ color: theme.palette.text.secondary, mb: 2.5, fontSize: 14.5 }}>
        🟡 Un carton jaune est un avertissement — 2 jaunes deviennent un rouge. 🔴 Un carton rouge (direct ou par
        2 jaunes) : la joueuse ne participe pas au prochain entraînement. Une fois la punition purgée, utilise
        « Réinitialiser ».
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 500 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {players.map((p) => {
            const status = computeCardStatus(cardsByUser.get(String(p.id)) || []);
            const isRed = status.effective === "red";
            const isYellow = status.effective === "yellow";
            return (
              <Box
                key={p.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  p: 1.5,
                  border: "1px solid",
                  borderColor: isRed
                    ? theme.palette.error.main
                    : isYellow
                    ? theme.palette.warning.main
                    : theme.palette.divider,
                  borderRadius: 2,
                  bgcolor: isRed ? "#fef2f2" : isYellow ? "#fffbeb" : theme.palette.background.paper,
                  boxShadow: theme.shadows[1],
                }}
              >
                <Avatar
                  src={p.photo ? resolveMediaUrl(p.photo) : undefined}
                  sx={{ width: 48, height: 48, border: `2px solid ${theme.palette.primary.main}` }}
                >
                  {initialsOf(p.nom_complet)}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 170 }}>
                  <Typography sx={{ fontWeight: 700 }}>{p.nom_complet}</Typography>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.25 }}>
                    {isRed ? (
                      <Chip
                        size="small"
                        label={`🔴 Carton rouge${status.reds === 0 ? " (2 jaunes)" : ""} — prochain entraînement interdit`}
                        sx={{ bgcolor: theme.palette.error.main, color: "#fff", fontWeight: 700 }}
                      />
                    ) : isYellow ? (
                      <Chip
                        size="small"
                        label="🟡 1 carton jaune"
                        sx={{ bgcolor: theme.palette.warning.main, color: "#fff", fontWeight: 700 }}
                      />
                    ) : (
                      <Chip size="small" label="Aucun carton" variant="outlined" />
                    )}
                  </Box>
                  {status.cards
                    .filter((c) => c.reason)
                    .map((c) => (
                      <Typography key={c.id} sx={{ fontSize: 12.5, color: theme.palette.text.secondary, mt: 0.25 }}>
                        {c.card_type === "red" ? "🔴" : "🟡"} {c.reason} — {String(c.created_at || "").slice(0, 10)}
                      </Typography>
                    ))}
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    sx={{ fontWeight: 700 }}
                    disabled={busy}
                    onClick={() => openAction(p, "yellow")}
                  >
                    🟡 Jaune
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    sx={{ fontWeight: 700 }}
                    disabled={busy}
                    onClick={() => openAction(p, "red")}
                  >
                    🔴 Rouge
                  </Button>
                  {status.cards.length > 0 && (
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      sx={{ fontWeight: 700 }}
                      disabled={busy}
                      onClick={() => openAction(p, "clear")}
                    >
                      Réinitialiser
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
          {players.length === 0 && <Alert severity="info">Aucune joueuse trouvée.</Alert>}
        </Box>
      )}

      <Dialog open={!!pending} onClose={() => (busy ? undefined : setPending(null))} fullWidth maxWidth="xs">
        {pending && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {pending.action === "clear"
                ? `Réinitialiser les cartons de ${pending.player.nom_complet} ?`
                : pending.action === "red"
                ? `Carton rouge pour ${pending.player.nom_complet} ?`
                : `Carton jaune pour ${pending.player.nom_complet} ?`}
            </DialogTitle>
            <DialogContent>
              {pending.action === "clear" ? (
                <Typography sx={{ fontSize: 14.5 }}>
                  La punition est passée : ses cartons actifs seront levés (ils restent dans l'historique).
                </Typography>
              ) : (
                <>
                  {pending.action === "yellow" && pendingStatus && pendingStatus.yellows >= 1 && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      C'est son 2e carton jaune : il devient un carton rouge — elle ne participera pas au prochain
                      entraînement.
                    </Alert>
                  )}
                  {pending.action === "red" && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      Carton rouge direct : elle ne participera pas au prochain entraînement.
                    </Alert>
                  )}
                  <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Motif (optionnel)"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    sx={{ mt: 0.5 }}
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPending(null)} disabled={busy}>
                Annuler
              </Button>
              <Button
                variant="contained"
                color={pending.action === "clear" ? "success" : pending.action === "red" ? "error" : "warning"}
                onClick={confirmAction}
                disabled={busy}
                sx={{ fontWeight: 700 }}
              >
                {busy ? <CircularProgress size={18} color="inherit" /> : "Confirmer"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
